import React, { useState, useRef, useEffect } from 'react';
import { 
  Download, 
  Upload, 
  X, 
  CheckCircle2, 
  AlertTriangle, 
  Loader2,
  FileSpreadsheet,
  ChevronRight,
  ChevronDown,
  AlertCircle,
  FileText,
  RefreshCw,
  ChevronUp
} from 'lucide-react';
import * as XLSX from 'xlsx';
import api from '../../api';

const BulkUploadModal = ({ 
  assignmentId, 
  onClose, 
  onSuccess, 
  tenantId, 
  isEndExam = false,
  batchId,        // ✅ NEW: batchId for lab filtering
  batchNumber     // ✅ NEW: batchNumber for lab filtering
}) => {
  const marksApiBase = isEndExam ? '/api/faculty/end-exam-marks' : '/api/faculty/marks-entry';
  const [step, setStep] = useState('upload');
  const [selectedFile, setSelectedFile] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedErrors, setExpandedErrors] = useState(true);
  const [expandedWarnings, setExpandedWarnings] = useState(true);
  const [autoCloseTimer, setAutoCloseTimer] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showUploadButton, setShowUploadButton] = useState(false);
  const [editMarks, setEditMarks] = useState({});
  const [currentErrorIdx, setCurrentErrorIdx] = useState(0);
  const [isRevalidated, setIsRevalidated] = useState(false);
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const [validationWarnings, setValidationWarnings] = useState([]);
  const [pendingUpload, setPendingUpload] = useState(false);
  
  // Store the original validated data separately
  const [originalValidatedData, setOriginalValidatedData] = useState(null);
  
  // Alert modal state
  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'error',
    details: null,
    expectedColumns: null
  });
  
  const fileInputRef = useRef(null);
  const scrollContainerRef = useRef(null);

  // Log batch props for debugging
  console.log('[BulkUploadModal] 🔍 Received props:', {
    batchId,
    batchNumber,
    assignmentId,
    isEndExam
  });

  useEffect(() => {
    return () => {
      if (autoCloseTimer) clearTimeout(autoCloseTimer);
    };
  }, [autoCloseTimer]);

  useEffect(() => {
    const handleWheelCancel = (e) => {
      if (document.activeElement && document.activeElement.type === 'number') {
        document.activeElement.blur();
      }
    };
    window.addEventListener('wheel', handleWheelCancel, { passive: true });
    return () => window.removeEventListener('wheel', handleWheelCancel);
  }, []);

  // Custom alert function
  const showAlert = (title, message, type = 'error', details = null, expectedColumns = null) => {
    setAlertModal({
      isOpen: true,
      title,
      message,
      type,
      details,
      expectedColumns
    });
  };

  const closeAlert = () => {
    setAlertModal(prev => ({ ...prev, isOpen: false }));
  };

  // ============================================================
  // UPDATED: handleDownloadTemplate with batchId and batchNumber
  // ============================================================
  const handleDownloadTemplate = async () => {
    setLoading(true);
    try {
      let url = `${marksApiBase}/${assignmentId}/download-template`;
      const params = new URLSearchParams();
      
      if (batchId) {
        params.append('batchId', batchId);
        console.log('[BulkUploadModal] 🎯 Adding batchId to template download:', batchId);
      }
      if (batchNumber) {
        params.append('batchNumber', batchNumber);
        console.log('[BulkUploadModal] 🎯 Adding batchNumber to template download:', batchNumber);
      }
      
      const queryString = params.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
      
      console.log('[BulkUploadModal] 📡 Downloading template from:', url);
      
      const response = await api.get(url, {
        responseType: 'blob',
        headers: { 'x-tenant-id': tenantId }
      });
      
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      let filename = `bulk_marks_template_${assignmentId}`;
      if (batchNumber) {
        filename += `_batch${batchNumber}`;
      }
      if (batchId) {
        filename += `_${batchId.substring(0, 8)}`;
      }
      filename += '.xlsx';
      link.setAttribute('download', filename);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      showAlert('Success', 'Template downloaded successfully!', 'success');
    } catch (error) {
      console.error('Download failed:', error);
      showAlert('Download Failed', 'Failed to download institutional spreadsheet template. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    processSelectedFile(file);
  };

  const processSelectedFile = (file) => {
    if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
      setSelectedFile(file);
      setStep('preview');
      setValidationResult(null);
      setEditMarks({});
      setShowUploadButton(false);
      setCurrentErrorIdx(0);
      setIsRevalidated(false);
      setOriginalValidatedData(null);
      setValidationWarnings([]);
      setShowWarningDialog(false);
      console.log('[BulkUpload] File selected:', file.name);
    } else if (file) {
      showAlert('Invalid Format', 'Please upload a valid Excel workbook format (.xlsx, .xls).', 'error');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    processSelectedFile(file);
  };

  // Create Excel from current state (always fresh)
  const createExcelFromCurrentData = async () => {
    const sourceData = originalValidatedData || validationResult;
    
    if (!sourceData?.preview?.students) {
      console.error('[BulkUpload] No source data available for Excel creation');
      throw new Error('No validation data available');
    }
    
    const allStudents = [...sourceData.preview.students];
    const marksHeaders = sourceData.preview.marksHeaders || [];
    
    console.log('[BulkUpload] Creating Excel from source:', {
      hasOriginalData: !!originalValidatedData,
      hasValidationResult: !!validationResult,
      studentCount: allStudents.length,
      editMarksCount: Object.keys(editMarks).length,
      editMarks: editMarks
    });
    
    Object.keys(editMarks).forEach(rowNumber => {
      const rowNum = parseInt(rowNumber);
      const studentIndex = allStudents.findIndex(s => s.rowNumber === rowNum);
      
      if (studentIndex !== -1) {
        console.log(`[BulkUpload] Applying edits for row ${rowNumber}:`, editMarks[rowNumber]);
        Object.keys(editMarks[rowNumber]).forEach(markIdx => {
          const idx = parseInt(markIdx);
          const markKey = `q_${idx}`;
          if (allStudents[studentIndex].marks[markKey]) {
            const oldValue = allStudents[studentIndex].marks[markKey].marksObtained;
            const newValue = parseFloat(editMarks[rowNumber][markIdx]);
            allStudents[studentIndex].marks[markKey].marksObtained = isNaN(newValue) ? null : newValue;
            console.log(`[BulkUpload] Updated student ${allStudents[studentIndex].studentUSN}, Q${idx}: ${oldValue} -> ${newValue}`);
          }
        });
      }
    });
    
    const worksheetData = [];
    worksheetData.push(['Student USN', 'Student Name', ...marksHeaders]);
    
    const sortedStudents = [...allStudents].sort((a, b) => a.rowNumber - b.rowNumber);
    sortedStudents.forEach(student => {
      const row = [student.studentUSN, student.studentName];
      const marksArray = Object.values(student.marks);
      marksArray.forEach(mark => {
        row.push(mark.marksObtained !== null && mark.marksObtained !== undefined ? mark.marksObtained : '');
      });
      worksheetData.push(row);
    });
    
    console.log('[BulkUpload] Excel created with rows:', worksheetData.length);
    
    const ws = XLSX.utils.aoa_to_sheet(worksheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Marks');
    
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const file = new File([blob], `corrected_marks_${Date.now()}.xlsx`, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    return file;
  };

  // ============================================================
  // UPDATED: handleValidate with batchId and batchNumber
  // ============================================================
  const handleValidate = async () => {
    if (!selectedFile) {
      showAlert('No File', 'Please select a file first.', 'warning');
      return;
    }
    
    setLoading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);
    
    try {
      // ✅ Build URL with batch params
      let url = `${marksApiBase}/${assignmentId}/validate-bulk-upload`;
      const params = new URLSearchParams();
      
      if (batchId) {
        params.append('batchId', batchId);
        console.log('[BulkUploadModal] 🎯 Adding batchId to validation:', batchId);
      }
      if (batchNumber) {
        params.append('batchNumber', batchNumber);
        console.log('[BulkUploadModal] 🎯 Adding batchNumber to validation:', batchNumber);
      }
      
      const queryString = params.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
      
      console.log('[BulkUploadModal] 📡 Validating with URL:', url);
      
      const response = await api.post(
        url,
        formData,
        { 
          headers: { 
            'Content-Type': 'multipart/form-data',
            'x-tenant-id': tenantId
          } 
        }
      );
      
      console.log('[BulkUpload] Validation response:', response.data);
      
      const validationData = response.data;
      const hasWarnings = validationData.validation?.hasWarnings || false;
      const warnings = validationData.validation?.warnings || [];
      const isValid = validationData.validation?.isValid || false;
      
      setValidationResult(validationData);
      setOriginalValidatedData(validationData);
      setEditMarks({});
      setCurrentErrorIdx(0);
      setIsRevalidated(false);
      
      if (hasWarnings && warnings.length > 0) {
        setValidationWarnings(warnings);
        setShowWarningDialog(true);
      } else if (isValid) {
        setShowUploadButton(true);
        showAlert('Validation Passed', 'File format is correct! You can proceed with upload.', 'success');
      } else {
        setShowUploadButton(false);
        showAlert('Validation Failed', 'Please fix the errors in your file.', 'error', validationData.validation?.errors);
      }
      
    } catch (error) {
      console.error('[BulkUpload] Validation failed:', error);
      const errorResponse = error.response?.data;
      
      if (errorResponse) {
        if (errorResponse.message?.includes('headers do not match') || errorResponse.expectedColumns) {
          showAlert(
            'Header Mismatch Detected',
            errorResponse.message || 'Excel headers do not match expected format',
            'error',
            errorResponse.errors,
            errorResponse.expectedColumns
          );
        } else {
          showAlert(
            'Validation Failed',
            errorResponse.message || 'Data scheme parsing failure. Please verify standard headers.',
            'error',
            errorResponse.errors
          );
        }
      } else {
        showAlert('Validation Failed', 'Network error. Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle continue despite warnings
  const handleContinueWithWarnings = () => {
    setShowWarningDialog(false);
    setShowUploadButton(true);
    setIsRevalidated(true);
    setPendingUpload(true);
    showAlert(
      'Proceeding with Warnings', 
      'You have chosen to continue despite warnings. Please review the upload results carefully.',
      'warning'
    );
  };

  // Handle fix issues (cancel upload)
  const handleFixIssues = () => {
    setShowWarningDialog(false);
    setValidationWarnings([]);
    setShowUploadButton(false);
  };

  // ============================================================
  // UPDATED: handleRevalidate with batchId and batchNumber
  // ============================================================
  const handleRevalidate = async () => {
    if (!originalValidatedData?.preview?.students) {
      showAlert('No Data', 'No data to re-validate. Please upload a file first.', 'warning');
      return;
    }
    
    setLoading(true);
    try {
      console.log('[BulkUpload] Starting re-validation with current edits...');
      console.log('[BulkUpload] Current editMarks before re-validation:', editMarks);
      
      const correctedFileData = await createExcelFromCurrentData();
      const formData = new FormData();
      formData.append('file', correctedFileData);
      
      // ✅ Build URL with batch params
      let url = `${marksApiBase}/${assignmentId}/validate-bulk-upload`;
      const params = new URLSearchParams();
      
      if (batchId) {
        params.append('batchId', batchId);
      }
      if (batchNumber) {
        params.append('batchNumber', batchNumber);
      }
      
      const queryString = params.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
      
      console.log('[BulkUploadModal] 📡 Re-validating with URL:', url);
      
      const response = await api.post(
        url,
        formData,
        { 
          headers: { 
            'Content-Type': 'multipart/form-data',
            'x-tenant-id': tenantId
          } 
        }
      );
      
      const result = response.data;
      console.log('[BulkUpload] Re-validation response:', result);
      
      const hasWarnings = result.validation?.hasWarnings || false;
      const warnings = result.validation?.warnings || [];
      const isValid = result.validation?.isValid || false;
      
      setOriginalValidatedData(result);
      setValidationResult(result);
      setEditMarks({});
      setCurrentErrorIdx(0);
      
      if (hasWarnings && warnings.length > 0) {
        setValidationWarnings(warnings);
        setShowWarningDialog(true);
        setIsRevalidated(false);
        setShowUploadButton(false);
      } else if (isValid) {
        setIsRevalidated(true);
        setShowUploadButton(true);
        showAlert('Re-validation Passed', 'All corrections are valid! You can now upload.', 'success');
      } else {
        setIsRevalidated(false);
        setShowUploadButton(false);
        showAlert('Re-validation Failed', 'Some corrections are still invalid. Please fix the highlighted errors.', 'error', result.validation?.errors);
      }
    } catch (error) {
      console.error('[BulkUpload] Re-validation failed:', error);
      const errorResponse = error.response?.data;
      showAlert(
        'Re-validation Failed',
        errorResponse?.message || 'Re-validation failed. Please try again.',
        'error',
        errorResponse?.errors
      );
      setShowUploadButton(false);
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // UPDATED: handleExecuteUpload with batchId and batchNumber
  // ============================================================
  const handleExecuteUpload = async () => {
    if (!originalValidatedData?.preview?.students || originalValidatedData.preview.students.length === 0) {
      showAlert('No Data', 'No student data to upload. Please validate a file first.', 'warning');
      return;
    }
    
    if (!showUploadButton) {
      showAlert('Validation Required', 'Please re-validate your corrections before uploading.', 'warning');
      return;
    }
    
    setLoading(true);
    setStep('processing');
    
    try {
      console.log('[BulkUpload] Starting final upload...');
      console.log('[BulkUpload] Upload data source:', {
        hasOriginalData: !!originalValidatedData,
        studentCount: originalValidatedData.preview.students.length,
        editMarksCount: Object.keys(editMarks).length,
        isRevalidated: isRevalidated
      });
      
      const finalFile = await createExcelFromCurrentData();
      
      const formData = new FormData();
      formData.append('file', finalFile);
      
      if (pendingUpload) {
        formData.append('skipWarnings', 'true');
      }
      
      // ✅ Build URL with batch params
      let url = `${marksApiBase}/${assignmentId}/bulk-upload`;
      const params = new URLSearchParams();
      
      if (batchId) {
        params.append('batchId', batchId);
      }
      if (batchNumber) {
        params.append('batchNumber', batchNumber);
      }
      if (pendingUpload) {
        params.append('skipWarnings', 'true');
      }
      
      const queryString = params.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
      
      console.log('[BulkUploadModal] 📡 Uploading with URL:', url);
      
      const response = await api.post(
        url,
        formData,
        { 
          headers: { 
            'Content-Type': 'multipart/form-data',
            'x-tenant-id': tenantId
          } 
        }
      );
      
      const result = response.data.data;
      console.log('[BulkUpload] Upload successful:', result);
      setUploadResult(result);
      const hasFailures = result.summary.failed > 0;
      
      if (!hasFailures) {
        setStep('success_closing');
        const timer = setTimeout(() => {
          if (onSuccess && result.summary.successful > 0) {
            console.log('[BulkUpload] Calling onSuccess callback');
            onSuccess();
          }
          onClose();
        }, 2200);
        setAutoCloseTimer(timer);
      } else {
        console.log('[BulkUpload] Upload completed with failures:', result.details);
        setStep('results_with_errors');
      }
    } catch (error) {
      console.error('[BulkUpload] Upload failed:', error);
      setUploadResult({
        summary: { total: 0, successful: 0, failed: 1, successRate: 0 },
        details: [{ 
          status: 'failed', 
          error: error.response?.data?.message || 'Academic engine upload processing timeout.' 
        }]
      });
      setStep('results_with_errors');
    } finally {
      setLoading(false);
      setPendingUpload(false);
    }
  };

  const handleClose = () => {
    if (autoCloseTimer) clearTimeout(autoCloseTimer);
    if (!loading) onClose();
  };

  const handleResetState = () => {
    console.log('[BulkUpload] Resetting state');
    setStep('upload');
    setValidationResult(null);
    setSelectedFile(null);
    setUploadResult(null);
    setEditMarks({});
    setShowUploadButton(false);
    setCurrentErrorIdx(0);
    setIsRevalidated(false);
    setOriginalValidatedData(null);
    setValidationWarnings([]);
    setShowWarningDialog(false);
    setPendingUpload(false);
  };

  const navigateToNextErrorField = () => {
    const errorInputs = scrollContainerRef.current?.querySelectorAll('.invalid-mark-input');
    if (!errorInputs || errorInputs.length === 0) return;

    let targetIdx = currentErrorIdx;
    if (targetIdx >= errorInputs.length) {
      targetIdx = 0;
    }

    const targetInput = errorInputs[targetIdx];
    if (targetInput) {
      targetInput.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center'
      });
      setTimeout(() => {
        targetInput.focus();
      }, 350);
      
      setCurrentErrorIdx((targetIdx + 1) % errorInputs.length);
    }
  };

  // Warning Dialog Component
  const WarningDialog = () => {
    if (!showWarningDialog) return null;
    
    const getSeverityColor = (severity) => {
      switch (severity) {
        case 'high': return 'bg-red-100 text-red-800 border-red-200';
        case 'medium': return 'bg-amber-100 text-amber-800 border-amber-200';
        case 'low': return 'bg-blue-100 text-blue-800 border-blue-200';
        default: return 'bg-gray-100 text-gray-800 border-gray-200';
      }
    };
    
    const getSeverityIcon = (severity) => {
      switch (severity) {
        case 'high': return <AlertCircle className="w-4 h-4" />;
        case 'medium': return <AlertTriangle className="w-4 h-4" />;
        default: return <AlertCircle className="w-4 h-4" />;
      }
    };
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4 animate-fadeIn">
        <div className="bg-white rounded-xl max-w-2xl w-full border border-amber-200 shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
          <div className="p-5 border-b border-slate-100">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-lg font-bold text-amber-900">Validation Warnings</h3>
                <p className="text-sm text-slate-600 mt-1">
                  The following issues were detected. You can continue with upload or fix them first.
                </p>
              </div>
              <button
                onClick={handleFixIssues}
                className="p-1 text-slate-400 hover:text-slate-600 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {validationWarnings.map((warning, idx) => (
              <div key={idx} className={`p-4 rounded-lg border ${getSeverityColor(warning.severity)}`}>
                <div className="flex items-center gap-2 mb-2">
                  {getSeverityIcon(warning.severity)}
                  <span className="text-xs font-bold uppercase tracking-wide">
                    {warning.type}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    warning.severity === 'high' ? 'bg-red-200 text-red-800' :
                    warning.severity === 'medium' ? 'bg-amber-200 text-amber-800' :
                    'bg-blue-200 text-blue-800'
                  }`}>
                    {warning.severity.toUpperCase()}
                  </span>
                </div>
                <p className="text-sm font-medium mb-2">{warning.message}</p>
                
                {warning.details?.missingStudents && Array.isArray(warning.details.missingStudents) && warning.details.missingStudents.length > 0 && (
                  <details className="mt-3">
                    <summary className="text-xs cursor-pointer font-medium hover:underline">
                      Show missing students ({warning.details.missingStudents.length})
                    </summary>
                    <div className="mt-2 max-h-32 overflow-y-auto bg-white rounded-lg p-2 border border-slate-200">
                      {warning.details.missingStudents.map((student, i) => (
                        <div key={i} className="text-xs text-slate-600 py-1 border-b border-slate-100 last:border-0">
                          <span className="font-mono">{student.usn || student.studentUSN || 'N/A'}</span> - {student.name || student.studentName || 'Unknown'}
                        </div>
                      ))}
                      {warning.details.note && (
                        <div className="text-xs text-amber-600 mt-1 pt-1 border-t border-slate-200">
                          {warning.details.note}
                        </div>
                      )}
                    </div>
                  </details>
                )}
                
                {warning.details?.examples && Array.isArray(warning.details.examples) && warning.details.examples.length > 0 && (
                  <details className="mt-3">
                    <summary className="text-xs cursor-pointer font-medium hover:underline">
                      Show examples ({warning.details.examples.length})
                    </summary>
                    <div className="mt-2 max-h-32 overflow-y-auto bg-white rounded-lg p-2 border border-slate-200">
                      {warning.details.examples.map((example, i) => (
                        <div key={i} className="text-xs text-slate-600 py-1 border-b border-slate-100 last:border-0">
                          Row {example.rowNumber}: {example.studentUSN || 'N/A'} - {example.marks}/{example.maxMarks} ({example.percentage}%)
                        </div>
                      ))}
                    </div>
                  </details>
                )}
                
                {warning.details?.extraStudents && Array.isArray(warning.details.extraStudents) && warning.details.extraStudents.length > 0 && (
                  <details className="mt-3">
                    <summary className="text-xs cursor-pointer font-medium hover:underline">
                      Show extra students ({warning.details.extraStudents.length})
                    </summary>
                    <div className="mt-2 max-h-32 overflow-y-auto bg-white rounded-lg p-2 border border-slate-200">
                      {warning.details.extraStudents.map((student, i) => (
                        <div key={i} className="text-xs text-slate-600 py-1">
                          {student.usn || student.studentUSN || 'N/A'}
                        </div>
                      ))}
                    </div>
                  </details>
                )}
                
                {warning.skipAction && (
                  <div className="mt-3 text-xs text-slate-500 bg-white/50 rounded p-2">
                    <span className="font-medium">What will happen:</span> {warning.skipAction}
                  </div>
                )}
              </div>
            ))}
          </div>
          
          <div className="border-t border-slate-100 p-4 flex justify-end gap-3">
            <button
              onClick={handleContinueWithWarnings}
              className="px-5 py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-semibold transition-colors"
            >
              Continue Anyway
            </button>
            <button
              onClick={handleFixIssues}
              className="px-5 py-2.5 bg-slate-200 hover:bg-slate-300 text-slate-800 rounded-lg text-sm font-semibold transition-colors"
            >
              Fix Issues
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Alert Modal Component
  const AlertModalComponent = () => {
    if (!alertModal.isOpen) return null;
    
    const getIcon = () => {
      switch (alertModal.type) {
        case 'success':
          return <CheckCircle2 className="w-6 h-6 text-emerald-600" />;
        case 'warning':
          return <AlertTriangle className="w-6 h-6 text-amber-600" />;
        case 'info':
          return <AlertCircle className="w-6 h-6 text-blue-600" />;
        default:
          return <AlertCircle className="w-6 h-6 text-red-600" />;
      }
    };
    
    const getBgColor = () => {
      switch (alertModal.type) {
        case 'success':
          return 'bg-emerald-50 border-emerald-200';
        case 'warning':
          return 'bg-amber-50 border-amber-200';
        case 'info':
          return 'bg-blue-50 border-blue-200';
        default:
          return 'bg-red-50 border-red-200';
      }
    };
    
    const getTitleColor = () => {
      switch (alertModal.type) {
        case 'success':
          return 'text-emerald-900';
        case 'warning':
          return 'text-amber-900';
        case 'info':
          return 'text-blue-900';
        default:
          return 'text-red-900';
      }
    };
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] animate-fadeIn p-4">
        <div className={`bg-white rounded-xl max-w-lg w-full border ${getBgColor()} shadow-xl transform animate-scaleUp`}>
          <div className="p-5">
            <div className="flex items-start gap-3">
              <div className="shrink-0">{getIcon()}</div>
              <div className="flex-1">
                <h3 className={`text-lg font-bold ${getTitleColor()} mb-2`}>
                  {alertModal.title}
                </h3>
                <p className="text-sm text-slate-700 mb-3">{alertModal.message}</p>
                
                {alertModal.expectedColumns && alertModal.expectedColumns.length > 0 && (
                  <div className="mt-3 p-3 bg-white rounded-lg border border-slate-200">
                    <p className="text-xs font-semibold text-slate-700 mb-2">Expected Format:</p>
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {alertModal.expectedColumns.map((col, idx) => (
                        <span key={idx} className="px-2 py-1 bg-slate-100 text-slate-700 rounded text-xs font-mono">
                          {col}
                        </span>
                      ))}
                    </div>
                    <button
                      onClick={() => {
                        closeAlert();
                        handleDownloadTemplate();
                      }}
                      className="w-full mt-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Download Correct Template
                    </button>
                  </div>
                )}
                
                {alertModal.details && alertModal.details.length > 0 && (
                  <div className="mt-3 p-3 bg-white rounded-lg border border-slate-200 max-h-40 overflow-y-auto">
                    <p className="text-xs font-semibold text-slate-700 mb-2">Details:</p>
                    <ul className="space-y-1">
                      {alertModal.details.map((detail, idx) => (
                        <li key={idx} className="text-xs text-red-600 flex items-start gap-1">
                          <span className="text-red-500">•</span>
                          {typeof detail === 'object' ? JSON.stringify(detail) : detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="border-t border-slate-100 p-4 flex justify-end">
            <button
              onClick={closeAlert}
              className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-sm font-semibold transition-colors"
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderProgressSteps = () => {
    const stepsDef = [
      { id: 'upload', label: 'File Selection' },
      { id: 'preview', label: 'Validation & Correction' },
      { id: 'complete', label: 'Registry Update' }
    ];

    const getCurrentActiveIdx = () => {
      if (step === 'upload') return 0;
      if (step === 'preview') return 1;
      return 2;
    };

    const activeIdx = getCurrentActiveIdx();

    return (
      <div className="w-full bg-slate-50 border-b border-slate-200/80 px-4 py-3 shrink-0">
        <div className="flex items-center justify-between max-w-xl mx-auto text-[11px] sm:text-xs font-medium text-slate-500">
          {stepsDef.map((s, idx) => (
            <React.Fragment key={s.id}>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <span className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full flex items-center justify-center font-semibold text-[9px] sm:text-[10px] ${
                  idx <= activeIdx ? 'bg-slate-900 text-white' : 'bg-slate-200 text-slate-600'
                }`}>
                  {idx + 1}
                </span>
                <span className={idx === activeIdx ? 'text-slate-900 font-semibold' : 'hidden xs:inline'}>{s.label}</span>
              </div>
              {idx < stepsDef.length - 1 && <ChevronRight className="w-3 h-3 text-slate-300" />}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  const getMarkValue = (student, markIdx) => {
    if (editMarks[student.rowNumber]?.[markIdx] !== undefined) {
      return editMarks[student.rowNumber][markIdx];
    }
    const markKey = `q_${markIdx}`;
    if (student.marks && student.marks[markKey]) {
      const value = student.marks[markKey].marksObtained;
      return value !== null && value !== undefined && value !== '' ? value : '';
    }
    return '';
  };

  const getMaxMarks = (student, markIdx) => {
    const markKey = `q_${markIdx}`;
    if (student.marks && student.marks[markKey]) {
      return student.marks[markKey].maxMarks;
    }
    return null;
  };

  const isMarkInvalid = (student, markIdx, markValue) => {
    if (student.isValid) return false;
    const maxMarks = getMaxMarks(student, markIdx);
    if (maxMarks && markValue && parseFloat(markValue) > maxMarks) return true;
    if (markValue && parseFloat(markValue) < 0) return true;
    return false;
  };

  const handleMarkEdit = (rowNumber, idx, value) => {
    console.log(`[BulkUpload] Edit: row ${rowNumber}, Q${idx}, value: ${value}`);
    setEditMarks(prev => ({
      ...prev,
      [rowNumber]: {
        ...(prev[rowNumber] || {}),
        [idx]: value
      }
    }));
    setShowUploadButton(false);
    setIsRevalidated(false);
  };

  const getLiveInvalidFieldsCount = () => {
    const sourceData = originalValidatedData || validationResult;
    if (!sourceData || sourceData.validation?.isValid) return 0;
    
    const allStudents = sourceData?.preview?.students || [];
    const marksHeaders = sourceData?.preview?.marksHeaders || [];
    let invalidCount = 0;

    allStudents.forEach(student => {
      if (!student.isValid) {
        let rowHasError = false;
        marksHeaders.forEach((_, markIdx) => {
          const value = getMarkValue(student, markIdx);
          if (isMarkInvalid(student, markIdx, value) || value === '') {
            rowHasError = true;
          }
        });
        if (rowHasError) invalidCount++;
      }
    });

    return invalidCount;
  };

  const renderStep = () => {
    switch (step) {
      case 'upload':
        return (
          <div className="space-y-4 animate-fadeIn">
            <div className="flex flex-col gap-3 p-4 bg-blue-50/50 border border-blue-100 rounded-xl sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                <div>
                  <h4 className="text-xs font-bold text-blue-900 sm:text-sm">Required Document Structure</h4>
                  <p className="text-[11px] sm:text-xs text-blue-700/90 leading-relaxed mt-0.5">
                    Ensure student indices match registration lists. Modified columns may throw processing flags.
                  </p>
                </div>
              </div>
              <button
                onClick={handleDownloadTemplate}
                disabled={loading}
                className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-blue-700 bg-white border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors shadow-sm w-full sm:w-auto active:scale-[0.98]"
              >
                <Download className="w-3.5 h-3.5" />
                Get Official Template
              </button>
            </div>

            <div 
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-xl p-6 sm:p-10 text-center transition-all duration-200 cursor-pointer ${
                isDragging 
                  ? 'border-blue-500 bg-blue-50/40 scale-[0.99]' 
                  : 'border-slate-200 hover:border-slate-300 bg-white'
              }`}
              onClick={() => fileInputRef.current?.click()}
            >
              <FileSpreadsheet className={`w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 transition-transform ${isDragging ? 'scale-110 text-blue-600' : 'text-slate-400'}`} />
              <p className="text-xs font-semibold text-slate-800 sm:text-sm">
                Drag and drop your grade spreadsheet here
              </p>
              <p className="text-[11px] text-slate-400 mt-1 mb-4">
                Supports Standard Excel Workbooks (.xlsx, .xls)
              </p>
              
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                className="hidden"
              />
              <button
                type="button"
                disabled={loading}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg text-xs font-semibold hover:bg-slate-800 transition-colors shadow-sm disabled:opacity-50 w-full sm:w-auto"
                onClick={(e) => {
                  e.stopPropagation();
                  fileInputRef.current?.click();
                }}
              >
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                Browse Local Storage
              </button>
            </div>
            
            {selectedFile && !validationResult && (
              <div className="flex justify-center pt-2">
                <button
                  type="button"
                  onClick={handleValidate}
                  disabled={loading}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-xs sm:text-sm transition-all shadow-sm w-full sm:w-auto"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  Validate Spreadsheet
                </button>
              </div>
            )}
          </div>
        );

      case 'preview':
        const sourceData = originalValidatedData || validationResult;
        const isValid = sourceData?.validation?.isValid;
        const hasFile = selectedFile && !sourceData;
        const hasEdits = Object.keys(editMarks).length > 0;
        const hasWarnings = sourceData?.validation?.hasWarnings;
        
        if (hasFile) {
          return (
            <div className="space-y-4 animate-fadeIn">
              <div className="p-6 text-center border bg-amber-50 border-amber-200 rounded-xl">
                <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 text-amber-600" />
                <h3 className="text-sm font-bold text-amber-900 sm:text-base mb-1">File Ready for Validation</h3>
                <p className="text-xs text-amber-700 mb-4 truncate max-w-xs mx-auto">
                  {selectedFile.name}
                </p>
                <button
                  type="button"
                  onClick={handleValidate}
                  disabled={loading}
                  className="inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-xs sm:text-sm transition-all shadow-sm w-full sm:w-auto"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  Validate Spreadsheet
                </button>
              </div>
            </div>
          );
        }
        
        if (!sourceData) {
          return (
            <div className="py-8 text-center">
              <p className="text-xs text-slate-500 sm:text-sm">No file selected. Please go back and select a file.</p>
              <button
                type="button"
                onClick={handleResetState}
                className="mt-4 inline-flex items-center px-4 py-2 bg-slate-900 text-white rounded-lg text-xs"
              >
                Go Back
              </button>
            </div>
          );
        }
        
        const allStudents = sourceData?.preview?.students || [];
        const marksHeaders = sourceData?.preview?.marksHeaders || [];
        const liveInvalidRowsCount = getLiveInvalidFieldsCount();
        
        return (
          <div className="space-y-4 animate-fadeIn">
            <div className={`p-3.5 border rounded-xl ${
              (isValid && isRevalidated) ? 'bg-emerald-50/40 border-emerald-200/80' : 
              (hasWarnings && !isValid) ? 'bg-amber-50/40 border-amber-200/80' :
              'bg-amber-50/40 border-amber-200/80'
            }`}>
              <div className="flex items-start gap-3">
                {(isValid && isRevalidated) ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                )}
                <div className="flex-1 min-w-0">
                  <h4 className={`text-xs sm:text-sm font-bold truncate ${
                    (isValid && isRevalidated) ? 'text-emerald-900' : 'text-amber-900'
                  }`}>
                    {(isValid && isRevalidated) ? '✓ Ready to Upload' : 
                     (hasWarnings && !isValid) ? '⚠ Warnings Detected - Can Still Upload' :
                     hasEdits ? '⚠ Edits Pending Validation' : '⚠ Validation Issues Detected'}
                  </h4>
                  <p className="text-[11px] sm:text-xs text-slate-600 mt-0.5 leading-tight">
                    Records: <span className="font-semibold text-slate-900">{sourceData?.validation?.totalRows}</span> | 
                    Valid: <span className="font-semibold text-emerald-700"> {sourceData?.validation?.totalRows - liveInvalidRowsCount}</span> | 
                    Pending Fixes: <span className="font-semibold text-amber-700"> {liveInvalidRowsCount}</span>
                    {hasWarnings && <span className="ml-2 font-semibold text-amber-700">| Warnings: {sourceData?.validation?.warnings?.length || 0}</span>}
                  </p>
                  {hasWarnings && !isValid && !isRevalidated && (
                    <p className="text-[10px] sm:text-xs text-amber-800 font-medium mt-1.5">
                      ⚠️ There are warnings but you can still upload. Review warnings and click "Continue with Warnings" if needed.
                    </p>
                  )}
                  {hasEdits && !isRevalidated && (
                    <p className="text-[10px] sm:text-xs text-amber-800 font-medium mt-1.5">
                      ✏️ You have made edits. Click "Re-validate Corrections" to verify your changes.
                    </p>
                  )}
                  {isRevalidated && isValid && (
                    <p className="text-[10px] sm:text-xs text-emerald-700 font-medium mt-1.5">
                      ✓ All corrections validated successfully! Ready to upload.
                    </p>
                  )}
                  {!hasEdits && !isValid && liveInvalidRowsCount > 0 && (
                    <p className="text-[10px] sm:text-xs text-amber-800 font-medium mt-1.5">
                      ✏️ Edit the highlighted fields below, then click "Re-validate Corrections".
                    </p>
                  )}
                </div>
              </div>
            </div>

            {sourceData?.validation?.warnings?.length > 0 && !isRevalidated && (
              <div className="overflow-hidden border border-amber-100 rounded-xl bg-amber-50/30">
                <button
                  type="button"
                  onClick={() => setExpandedWarnings(!expandedWarnings)}
                  className="w-full flex items-center justify-between px-3.5 py-2.5 bg-amber-50/60 text-[11px] font-bold text-amber-800 border-b border-amber-100 hover:bg-amber-100/50 transition-colors"
                >
                  <span className="flex items-center gap-2">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                    Warnings ({sourceData.validation.warnings.length})
                  </span>
                  {expandedWarnings ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                </button>
                {expandedWarnings && (
                  <div className="max-h-48 overflow-y-auto divide-y divide-amber-100/60 bg-white">
                    {sourceData.validation.warnings.map((warning, idx) => (
                      <div key={idx} className="p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${
                            warning.severity === 'high' ? 'bg-red-100 text-red-700' :
                            warning.severity === 'medium' ? 'bg-amber-100 text-amber-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {warning.severity.toUpperCase()}
                          </span>
                          <span className="font-bold text-amber-800">{warning.type}</span>
                        </div>
                        <p className="text-amber-700 text-xs">{warning.message}</p>
                        {warning.details?.missingStudentsCount && (
                          <p className="text-amber-600 text-[10px] mt-1">
                            Missing: {warning.details.missingStudentsCount} students
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {sourceData?.validation?.errors?.length > 0 && !isValid && (
              <div className="overflow-hidden border border-red-100 rounded-xl bg-red-50/30">
                <button
                  type="button"
                  onClick={() => setExpandedErrors(!expandedErrors)}
                  className="w-full flex items-center justify-between px-3.5 py-2.5 bg-red-50/60 text-[11px] font-bold text-red-800 border-b border-red-100 hover:bg-red-100/50 transition-colors"
                >
                  <span className="flex items-center gap-2">
                    <AlertCircle className="w-3.5 h-3.5 text-red-600" />
                    Issues to Fix ({sourceData.validation.errors.length})
                  </span>
                  {expandedErrors ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                </button>
                {expandedErrors && (
                  <div className="max-h-36 overflow-y-auto divide-y divide-red-100/60 font-mono text-[10px] text-red-700 bg-white">
                    {sourceData.validation.errors.map((err, idx) => (
                      <div key={idx} className="p-2 flex flex-col gap-0.5 hover:bg-red-50/40">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="font-bold bg-red-100 px-1 rounded text-red-900">Row {err.rowNumber}</span>
                          <span className="font-semibold text-slate-800">{err.studentUSN || 'Unknown ID'}</span>
                          <span className="truncate text-slate-500 max-w-[120px]">{err.studentName || ''}</span>
                        </div>
                        <div className="font-sans text-[11px] text-red-600 pl-1">
                          {err.errors?.map((e, i) => (
                            <div key={i} className="mt-0.5">• {e}</div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {allStudents.length > 0 && (
              <div className="flex flex-col flex-1 min-h-0 border border-slate-200 rounded-xl bg-white shadow-sm overflow-hidden relative">
                <div className="bg-slate-50 px-3 py-2 border-b border-slate-200 shrink-0 flex justify-between items-center">
                  <h4 className="text-[10px] font-bold text-slate-700 uppercase tracking-wider">
                    {liveInvalidRowsCount > 0 ? 'Review Spreadsheet Rejections' : 'Validated Matriculation Manifest'}
                  </h4>
                </div>
                
                <div 
                  ref={scrollContainerRef}
                  className="overflow-x-auto overflow-y-auto max-h-[40vh] sm:max-h-[50vh] scrollbar-thin relative pb-12 sm:pb-0"
                >
                  <table className="min-w-full divide-y divide-slate-200 text-left text-xs whitespace-nowrap table-auto">
                    <thead className="bg-slate-50/70 font-bold text-slate-600 border-b border-slate-100 sticky top-0 z-20">
                      <tr>
                        <th className="px-3 py-2 bg-slate-100 sticky left-0 z-30 border-r border-slate-200/60">Row</th>
                        <th className="px-3 py-2 bg-slate-100 sticky left-[45px] mountaineer-shadow z-30 border-r border-slate-200/60">Student ID / USN</th>
                        <th className="px-3 py-2 bg-slate-100 font-semibold text-slate-600">Full Name</th>
                        {marksHeaders.map((header, idx) => (
                          <th key={idx} className="px-4 py-2 text-center min-w-[90px] font-mono font-bold bg-slate-50/80">
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-slate-700 font-medium">
                      {allStudents.map((student, idx) => {
                        const hasValidationErrors = !student.isValid;
                        
                        return (
                          <tr key={idx} className={`hover:bg-slate-50/80 transition-colors ${hasValidationErrors ? 'bg-amber-50/30' : ''}`}>
                            <td className={`px-3 py-2.5 font-mono text-[11px] text-slate-400 text-center sticky left-0 z-10 border-r border-slate-100 ${
                              hasValidationErrors ? 'bg-amber-100/50' : 'bg-white'
                            }`}>
                              {student.rowNumber}
                            </td>
                            <td className={`px-3 py-2.5 font-mono font-semibold sticky left-[45px] z-10 border-r border-slate-100 ${
                              hasValidationErrors ? 'bg-amber-100/50 text-amber-800' : 'bg-white text-slate-900'
                            }`}>
                              {student.studentUSN || '-'}
                            </td>
                            <td className="px-3 py-2.5 text-slate-600 max-w-[150px] truncate">
                              {student.studentName || '-'}
                            </td>
                            {marksHeaders.map((header, markIdx) => {
                              const markValue = getMarkValue(student, markIdx);
                              const maxMarks = getMaxMarks(student, markIdx);
                              const isMarkInvalidField = hasValidationErrors && (isMarkInvalid(student, markIdx, markValue) || markValue === '');
                              const isEditable = hasValidationErrors;
                              
                              return (
                                <td key={markIdx} className="px-3 py-1 text-center align-middle">
                                  {isEditable ? (
                                    <div className="inline-block vertical-align-middle">
                                      <input
                                        type="number"
                                        min="0"
                                        max={maxMarks || 100}
                                        step="0.5"
                                        className={`w-20 text-center rounded border px-1.5 py-1 font-mono text-xs font-bold transition-all focus:outline-none focus:ring-2 no-wheel-step ${
                                          isMarkInvalidField 
                                            ? 'border-red-400 bg-red-50 text-red-700 focus:border-red-500 focus:ring-red-100 invalid-mark-input' 
                                            : 'border-amber-300 bg-amber-50 text-amber-800 focus:border-amber-500 focus:ring-amber-100'
                                        }`}
                                        value={markValue}
                                        onWheel={(e) => e.currentTarget.blur()}
                                        onChange={(e) => handleMarkEdit(student.rowNumber, markIdx, e.target.value)}
                                        placeholder="Marks"
                                      />
                                      {maxMarks && (
                                        <div className="text-[9px] text-slate-400 mt-0.5 leading-none">Max: {maxMarks}</div>
                                      )}
                                    </div>
                                  ) : (
                                    <div className={`inline-flex flex-col items-center justify-center px-2 py-1 rounded font-mono text-xs font-bold min-w-[65px] ${
                                      markValue !== '' && markValue !== undefined && markValue !== null 
                                        ? 'bg-slate-100 text-slate-800' 
                                        : 'bg-slate-50 text-slate-400'
                                    }`}>
                                      <span>{markValue !== '' && markValue !== undefined && markValue !== null ? markValue : '-'}</span>
                                      {maxMarks && (
                                        <span className="text-[9px] font-normal text-slate-400">/{maxMarks}</span>
                                      )}
                                    </div>
                                  )}
                                  {isMarkInvalidField && maxMarks && markValue !== '' && (
                                    <div className="text-red-500 text-[9px] mt-0.5 whitespace-normal max-w-[90px] mx-auto leading-none">
                                      0-{maxMarks} only
                                    </div>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {!isValid && liveInvalidRowsCount > 0 && (
                  <div className="absolute bottom-0 left-0 right-0 bg-amber-900 text-white px-3 py-2 flex items-center justify-between text-xs font-semibold shadow-xl border-t border-amber-800 sm:hidden z-40 animate-fadeIn">
                    <div className="flex items-center gap-1.5 text-amber-200">
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-400 animate-pulse" />
                      <span>{liveInvalidRowsCount} fields pending review</span>
                    </div>
                    <button
                      type="button"
                      onClick={navigateToNextErrorField}
                      className="bg-white/10 hover:bg-white/20 active:bg-white/30 text-white font-bold px-2.5 py-1 rounded flex items-center gap-1 text-[11px] backdrop-blur-xs border border-white/10 transition-colors"
                    >
                      <span>Find Next</span>
                      <ChevronUp className="w-3.5 h-3.5 rotate-90" />
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="flex flex-col gap-2 pt-3 border-t border-slate-100 sm:flex-row-reverse sm:justify-between">
              <div className="flex flex-col gap-2 w-full sm:flex-row sm:w-auto">
                {(Object.keys(editMarks).length > 0 || (!isRevalidated && !isValid)) && (
                  <button
                    type="button"
                    onClick={handleRevalidate}
                    disabled={loading}
                    className="w-full sm:w-auto px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-xs transition-all shadow-sm flex items-center justify-center gap-1.5"
                  >
                    {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                    Re-validate Corrections
                  </button>
                )}
                
                {showUploadButton && (
                  <button
                    type="button"
                    onClick={handleExecuteUpload}
                    disabled={loading}
                    className="w-full sm:w-auto px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold text-xs transition-all shadow-sm flex items-center justify-center gap-1.5"
                  >
                    {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                    Upload to Database
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={handleResetState}
                className="w-full sm:w-auto px-4 py-2.5 text-xs font-bold text-slate-600 hover:bg-slate-50 border border-slate-200 rounded-xl transition-colors text-center"
              >
                Upload Different File
              </button>
            </div>
          </div>
        );

      case 'processing':
        return (
          <div className="text-center py-10 px-4 max-w-xs mx-auto animate-fadeIn">
            <div className="relative flex items-center justify-center w-14 h-14 mx-auto mb-4 bg-slate-50 rounded-full border border-slate-100">
              <Loader2 className="w-7 h-7 text-slate-900 animate-spin" />
            </div>
            <h3 className="text-sm font-bold text-slate-950 sm:text-base">Synchronizing Ledger</h3>
            <p className="text-[11px] text-slate-500 leading-relaxed mt-1.5">
              Validating grading schemas, performing automated CIE weight scaling calculations, and appending metadata records securely.
            </p>
          </div>
        );

      case 'success_closing':
        return (
          <div className="text-center py-10 px-4 max-w-xs mx-auto animate-scaleUp">
            <div className="w-12 h-12 bg-emerald-50 border border-emerald-200 rounded-full flex items-center justify-center mx-auto mb-3 shadow-sm shadow-emerald-100">
              <CheckCircle2 className="w-6 h-6 text-emerald-600" />
            </div>
            <h3 className="text-sm font-bold text-slate-950 sm:text-base">Manifest Fully Saved</h3>
            <p className="text-[11px] text-slate-600 leading-relaxed mt-1">
              Successfully wrote <span className="font-bold text-emerald-700">{uploadResult?.summary?.successful}</span> student data nodes directly to tracking registries.
            </p>
            <div className="mt-5 inline-flex items-center gap-1.5 text-[9px] uppercase font-bold tracking-widest text-slate-400 bg-slate-50 px-3 py-1 rounded-full border border-slate-100">
              <Loader2 className="w-2.5 h-2.5 animate-spin text-slate-400" />
              Closing Session
            </div>
          </div>
        );

      case 'results_with_errors':
        const partialSuccess = uploadResult?.summary?.successful > 0;
        return (
          <div className="space-y-4 animate-fadeIn">
            <div className={`p-3.5 border rounded-xl ${
              partialSuccess ? 'bg-amber-50/40 border-amber-200' : 'bg-rose-50/40 border-rose-200'
            }`}>
              <div className="flex items-start gap-3">
                <AlertCircle className={`w-5 h-5 shrink-0 mt-0.5 ${partialSuccess ? 'text-amber-600' : 'text-rose-600'}`} />
                <div className="flex-1 min-w-0">
                  <h4 className={`text-xs sm:text-sm font-bold truncate ${partialSuccess ? 'text-amber-900' : 'text-rose-900'}`}>
                    {partialSuccess ? 'Transaction Completed with Failures' : 'Database Write Rejected'}
                  </h4>
                  <p className="text-[11px] text-slate-600 mt-0.5 leading-tight">
                    Passed: <span className="font-semibold text-emerald-700">{uploadResult?.summary?.successful || 0}</span> | 
                    Failures: <span className="font-semibold text-rose-700">{uploadResult?.summary?.failed || 0} rows</span>.
                  </p>
                </div>
              </div>
            </div>

            {uploadResult?.details && (
              <div className="border border-rose-100 rounded-xl overflow-hidden bg-rose-50/10">
                <div className="bg-rose-50/40 border-b border-rose-100 px-3 py-2 shrink-0">
                  <h4 className="text-[10px] font-bold text-rose-900 uppercase tracking-wider">Unresolved System Row Rejections</h4>
                </div>
                <div className="max-h-40 overflow-y-auto bg-white p-0.5 font-mono text-[10px] divide-y divide-rose-100/40">
                  {uploadResult.details.filter(d => d.status === 'failed').map((detail, idx) => (
                    <div key={idx} className="p-2 flex flex-col gap-0.5 hover:bg-rose-50/30 sm:flex-row sm:items-start sm:justify-between">
                      <span className="font-bold text-rose-900 bg-rose-100/70 px-1 py-0.5 rounded text-[9px] shrink-0 w-max">
                        {detail.studentUSN || 'SYSTEM EXCEPTION'}
                      </span>
                      <span className="text-rose-700 font-sans leading-relaxed text-left sm:text-right max-w-xs">{detail.error}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {partialSuccess && (
              <div className="p-2.5 bg-emerald-50/40 border border-emerald-100 rounded-xl text-[11px] text-emerald-800 flex items-center gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                <span>Partial safe states verified: {uploadResult.summary.successful} entries committed securely.</span>
              </div>
            )}

            <div className="flex flex-col gap-2 pt-3 border-t border-slate-100 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={handleResetState}
                className="w-full sm:w-auto order-2 sm:order-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-xl transition-all shadow-sm text-center"
              >
                Upload New File
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="w-full sm:w-auto order-1 sm:order-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-xl font-bold text-xs transition-colors text-center"
              >
                Close Context
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{__html: `
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleUp {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        .animate-fadeIn { animation: fadeIn 0.2s ease-out; }
        .animate-scaleUp { animation: scaleUp 0.2s ease-out; }
        @media (max-width: 639px) {
          .mountaineer-shadow {
            box-shadow: 4px 0 8px -3px rgba(0,0,0,0.15);
          }
        }
        .scrollbar-thin::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        .scrollbar-thin::-webkit-scrollbar-track {
          background: #f1f5f9;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 3px;
        }
        .no-wheel-step::-webkit-outer-spin-button,
        .no-wheel-step::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        .no-wheel-step {
          -moz-appearance: textfield;
        }
      `}} />

      {/* Warning Dialog */}
      <WarningDialog />
      
      {/* Alert Modal */}
      <AlertModalComponent />

      <div 
        className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-end sm:items-center justify-center z-50 p-0 sm:p-4 animate-fadeIn" 
        onClick={handleClose}
      >
        <div 
          className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-4xl max-h-[92vh] sm:max-h-[85vh] flex flex-col overflow-hidden transform transition-all" 
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-white border-b border-slate-200/80 px-4 py-3.5 flex justify-between items-center shrink-0">
            <div>
              <h2 className="text-sm font-bold text-slate-900 sm:text-base">Academic Core Bulk Entry</h2>
              <p className="text-[10px] sm:text-[11px] text-slate-500 font-medium mt-0.5">Automated Assignment & CIE Processing System</p>
            </div>
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg transition-colors disabled:opacity-40"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {renderProgressSteps()}

          <div className="p-4 overflow-y-auto flex-1 bg-white scrollbar-thin">
            {renderStep()}
          </div>
        </div>
      </div>
    </>
  );
};

export default BulkUploadModal;